import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import styles from "./Thumbnails.module.scss";
import smoothscroll from "smoothscroll-polyfill";
import classNames from "classnames";
import { useCanScroll } from "./useCanScroll";
import { ScrollButton } from "./ScrollButton";
import {
  getElementScrollDirection,
  scrollElementIntoViewWithinContainer,
  scrollElementToTheStartOfContainer,
} from "../../../../utils/scroll/scroll";
import { GalleryObject } from "../../utils";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";

const generateSrcSet = (imageUrl: string) => {
  return `${imageUrl}, ${imageUrl
    .replace("wid=120", "wid=180")
    .replace("hei=160", "hei=240")} 1.5x, ${imageUrl
    .replace("wid=120", "wid=240")
    .replace("hei=160", "hei=320")} 2x`;
};

interface ThumbnailsProps {
  inModal?: boolean;
  mode?: "image" | "video";
  galleryObjects: GalleryObject[];
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  className?: string;
}

export const Thumbnails = ({
  inModal = false,
  mode,
  galleryObjects,
  selectedIndex,
  setSelectedIndex,
  className = undefined,
}: ThumbnailsProps) => {
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const activeElementRef = useRef(null);

  const isServer = () => !(typeof window != "undefined" && window.document);
  if (!isServer) smoothscroll.polyfill();

  const { canScrollUp, canScrollDown } = useCanScroll({
    thumbnailsRef,
    galleryObjects,
  });

  useEffect(() => {
    if (!thumbnailsRef.current || !activeElementRef.current) {
      return;
    }

    const direction = getElementScrollDirection(thumbnailsRef.current);

    if (direction === "horizontal") {
      scrollElementIntoViewWithinContainer({
        direction,
        container: thumbnailsRef.current,
        element: activeElementRef.current,
        mode: "middle",
      });
    } else if (direction === "vertical") {
      scrollElementToTheStartOfContainer({
        direction,
        container: thumbnailsRef.current,
        element: activeElementRef.current,
        offset: 36,
      });
    }
  }, [selectedIndex, galleryObjects]);

  if (!galleryObjects || galleryObjects.length <= 1) {
    return null;
  }

  const handleThumbnailClick = (idx: number) => {
    setSelectedIndex(idx);
    dispatchGalleryMonetateQEvent(
      "ThumbnailItem",
      inModal && mode ? `${mode}Modal` : "generic",
    );
  };

  const scrollButtonClick = (direction: "next" | "previous") => () => {
    const distance = thumbnailsRef.current!.clientHeight / 2;
    const scrollTop = thumbnailsRef.current!.scrollTop;

    if (direction === "next") {
      thumbnailsRef.current!.scrollTo({
        top: scrollTop + distance,
        behavior: "smooth",
      });
      dispatchGalleryMonetateQEvent(
        "ThumbnailsDownArrow",
        inModal && mode ? `${mode}Modal` : "generic",
      );
    } else {
      thumbnailsRef.current!.scrollTo({
        top: scrollTop - distance,
        behavior: "smooth",
      });
      dispatchGalleryMonetateQEvent(
        "ThumbnailsUpArrow",
        inModal && mode ? `${mode}Modal` : "generic",
      );
    }
  };

  return (
    <div
      data-testid="standard:gallery:thumbnails"
      className={classNames(styles.container, className, {
        [styles.inZoomModal]: inModal,
      })}
    >
      <ScrollButton
        isZoomModal={inModal}
        direction="previous"
        disabled={!canScrollUp}
        onClick={scrollButtonClick("previous")}
      />
      <ScrollButton
        isZoomModal={inModal}
        direction="next"
        disabled={!canScrollDown}
        onClick={scrollButtonClick("next")}
      />
      <div
        className={classNames(styles.thumbnailTrack, {
          [styles.inZoomModal]: inModal,
        })}
        data-testid="standard:gallery:thumbnails:track"
        ref={thumbnailsRef}
      >
        {galleryObjects.map((image, idx) => {
          return (
            <button
              ref={idx === selectedIndex ? activeElementRef : null}
              key={`thumbnail-btn-${idx}`}
              className={classNames(styles.button, styles.thumbnailImage, {
                [styles.video]: image.type === "video",
                [styles.active]: idx === selectedIndex,
                [styles.inZoomModal]: inModal,
              })}
              onClick={() => handleThumbnailClick(idx)}
              data-testid={`standard:gallery:thumbnails:thumbnail-${idx}`}
              aria-label={
                idx === 0
                  ? `thumbnail ${idx + 1} of ${galleryObjects.length}`
                  : `thumbnail ${idx + 1} `
              }
            >
              <img
                key={`thumbnail-${idx}`}
                src={image.thumbnailUrl
                  .replace("wid=120", "wid=240")
                  .replace("hei=160", "hei=320")}
                alt={image.title}
                srcSet={generateSrcSet(image.thumbnailUrl)}
                className={styles.thumbnail}
                loading="lazy"
                sizes="(max-width: 767px) 100vw, (min-width: 768px) 50vw"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};
